import React, { useEffect, Suspense, lazy } from 'react';

// Critical UI (Synchronous for instant launch)
import { SessionModals } from './shared/components/SessionModals';
import { MasterMissionBoard } from './features/agenda/MasterMissionBoard';
import { useMasterPlanRouter } from './hooks/useMasterPlanRouter';
import { useUIStore } from '@stores/v4/useUIStore';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { EventComposer } from './features/composer/EventComposer';
import { Loader2 } from 'lucide-react';

// Lazy-Loaded Views
const LibraryView = lazy(() => import('./features/library/LibraryView').then(m => ({ default: m.LibraryView })));
const SessionView = lazy(() => import('./features/session/SessionView').then(m => ({ default: m.SessionView })));
const AthleteProfileView = lazy(() => import('./features/profile/AthleteProfileView').then(m => ({ default: m.AthleteProfileView })));
const ProgramEditorView = lazy(() => import('./features/builder/ProgramEditorView').then(m => ({ default: m.ProgramEditorView })));

interface MasterPlanAppProps {
    onExit: () => void;
    currentView: string | null;
    onNavigate: (view: string | null, state?: any) => void;
    navState?: any;
}

/**
 * MasterPlanApp: The main application orchestrator.
 * HARDENED: Viewport-constrained and code-split for performance.
 * FIXED: Passed missing props to SessionView to restore modal triggers and timers.
 */
const MasterPlanApp: React.FC<MasterPlanAppProps> = ({ onExit, currentView, onNavigate, navState }) => {
    const lastView = useUIStore(state => state.lastView);
    const isComposerOpen = useComposerStore(state => state.isComposerOpen);
    
    const router = useMasterPlanRouter({ currentView, onNavigate, lastView });

    // Safety: If stuck in 'session' view with no active session, redirect to agenda
    useEffect(() => {
        if (currentView === 'session' && !router.activeSession && !router.activeRecoverySession) {
            console.warn("[MasterPlanApp] Stuck in session view with no active session. Redirecting...");
            onNavigate('master-agenda');
        }
    }, [currentView, router.activeSession, router.activeRecoverySession, onNavigate]);

    // Back Button Interception
    useEffect(() => {
        (window as any).onNavigate = onNavigate;
        const handleBack = (e: PopStateEvent) => {
            if (currentView && currentView !== 'master-agenda') {
                e.preventDefault();
                onNavigate('master-agenda');
            }
        };
        window.addEventListener('popstate', handleBack);
        return () => window.removeEventListener('popstate', handleBack);
    }, [currentView, onNavigate]);

    const renderLoading = () => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#000' }}>
            <Loader2 size={32} color="#f29b11" className="animate-spin" />
        </div>
    );

    const renderView = () => {
        return (
            <Suspense fallback={renderLoading()}>
                {(() => {
                    switch (currentView) {
                        case 'library':
                            return <LibraryView onNavigate={onNavigate} />;
                        case 'session':
                            return (
                                <SessionView 
                                    onNavigate={onNavigate}
                                    performanceLabel={router.performanceLabel}
                                    elapsed={router.elapsed}
                                    sleepElapsed={router.sleepElapsed}
                                    setShowAbandonModal={router.setShowAbandonModal}
                                    setShowFinishModal={router.setShowFinishModal}
                                    onToggleSleep={router.handleToggleSleep}
                                    lastView={lastView}
                                />
                            );
                        case 'profile':
                            return <AthleteProfileView onBack={() => onNavigate('master-agenda')} />;
                        case 'builder':
                            return <ProgramEditorView onNavigate={onNavigate} navState={navState} />;
                        default:
                            return (
                                <MasterMissionBoard
                                    onExit={onExit}
                                    onNavigate={onNavigate}
                                    onLogActivity={router.handlePrepareActivity}
                                    onInitiateClone={(event, date) => router.setShowCloneConfirm({ event, date })}
                                    setConfirmDeleteLog={router.setConfirmDeleteLog}
                                    onStartPlannedSession={router.handleStartPlannedSession}
                                    onMaximizeRecovery={router.maximizeRecovery}
                                    onMaximizeSession={router.maximizeSession}
                                    onToggleSleep={router.handleToggleSleep}
                                    setShowAbandonModal={router.setShowAbandonModal}
                                    activeSession={router.activeSession}
                                    performanceLabel={router.performanceLabel}
                                    elapsed={router.elapsed}
                                    activeRecoverySession={router.activeRecoverySession}
                                    sleepElapsed={router.sleepElapsed}
                                    handleQuickMetabolic={router.handleQuickMetabolic}
                                    handleQuickNutrition={router.handleQuickNutrition}
                                    handleQuickSupplement={router.handleQuickSupplement}
                                    handleReschedule={router.handleReschedule}
                                    handleManualLog={router.handleManualLog}
                                    handleMarkMissed={router.handleMarkMissed}
                                    handleMarkCompleted={router.handleMarkCompleted}
                                />
                            );
                    }
                })()}
            </Suspense>
        );
    };

    const isBuilderView = currentView === 'builder';

    return (
        <div 
            className={`master-plan-orchestrator ${!isBuilderView ? 'viewport-constrained' : ''}`} 
            style={{ 
                maxWidth: isBuilderView ? 'none' : '500px', 
                margin: '0 auto', 
                height: '100dvh', 
                backgroundColor: '#000', 
                position: 'relative', 
                overflow: 'hidden' 
            }}
        >
            <SessionModals
                showAbandonModal={router.showAbandonModal}
                setShowAbandonModal={router.setShowAbandonModal}
                handleAbandonSession={router.handleAbandonSession}
                showFinishModal={router.showFinishModal}
                setShowFinishModal={router.setShowFinishModal}
                handleFinalizeSession={router.handleFinalizeSession}
                isSaving={router.isSaving}
                confirmDeleteLog={router.confirmDeleteLog}
                setConfirmDeleteLog={router.setConfirmDeleteLog}
                handleDeleteLog={router.handleDeleteLog}
                isDeleting={router.isDeleting}
                showSleepConfirm={router.showSleepConfirm}
                setShowSleepConfirm={router.setShowSleepConfirm}
                handleStartSleep={router.handleInitiateSleep}
                showWakeUpConfirm={router.showWakeUpConfirm}
                setShowWakeUpConfirm={router.setShowWakeUpConfirm}
                handleEndSleep={router.handleConfirmWakeUp}
                showSessionConflict={router.showSessionConflict}
                setShowSessionConflict={router.setShowSessionConflict}
                onMaximizeRecovery={router.maximizeRecovery}
                onMaximizeSession={router.maximizeSession}
                showCloneConfirm={router.showCloneConfirm}
                setShowCloneConfirm={router.setShowCloneConfirm}
                handleConfirmClone={router.handleConfirmClone}
            />
            {renderView()}
            {isComposerOpen && <EventComposer onNavigate={onNavigate} />}
        </div>
    );
};

export default MasterPlanApp;
